import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag/use-flags';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { messages } from './messages';

export const SESSION_EXPIRED_FLAG_ID = 'twg-microsite.session-expired';
export const LOGIN_FAILED_FLAG_ID = 'twg-microsite.login-failed';

/**
 * Offers the supported recovery path for the two session problems
 * `UserProvider` can report: the session expiring during use, and the
 * automatic login redirect failing repeatedly. Renders nothing itself: each
 * problem raises one flag whose action runs the existing Identity login
 * redirect, which preserves the current URL through `continue`.
 *
 * Must sit below `LocaleProvider` (for copy) and `FlagsProvider` (for the
 * flags), which rules out living inside `UserProvider`.
 */
export const SessionFlags = (): null => {
	const { sessionStatus, automaticLoginFailed, sessionExpiredReturnUrl, attemptUserLogin } =
		useAtlassianUserContext();
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const hasShownExpired = useRef(false);
	const hasShownLoginFailed = useRef(false);

	useEffect(() => {
		if (sessionStatus !== 'expired' || hasShownExpired.current) {
			return;
		}
		hasShownExpired.current = true;

		showFlag({
			id: SESSION_EXPIRED_FLAG_ID,
			appearance: 'warning',
			title: formatMessage(messages.expiredTitle),
			description: formatMessage(messages.expiredDescription),
			actions: [
				{
					content: formatMessage(messages.logIn),
					onClick: () => {
						// Back to where the session expired, not to wherever signed-out
						// routing has moved the visitor since.
						attemptUserLogin(sessionExpiredReturnUrl ?? undefined);
					},
				},
			],
		});
	}, [sessionStatus, sessionExpiredReturnUrl, showFlag, formatMessage, attemptUserLogin]);

	useEffect(() => {
		if (!automaticLoginFailed || hasShownLoginFailed.current) {
			return;
		}
		hasShownLoginFailed.current = true;

		showFlag({
			id: LOGIN_FAILED_FLAG_ID,
			appearance: 'warning',
			title: formatMessage(messages.loginFailedTitle),
			description: formatMessage(messages.loginFailedDescription),
			actions: [
				{
					content: formatMessage(messages.tryAgain),
					onClick: () => {
						attemptUserLogin();
					},
				},
			],
		});
	}, [automaticLoginFailed, showFlag, formatMessage, attemptUserLogin]);

	return null;
};
